/**
 * Integrity Verification Panel
 * Cryptographic proof for Superpower #3 (Audit Defensibility)
 */
import React, { useState } from 'react';
import { Shield, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Hash } from 'lucide-react';
import { apiFetch } from '../../services/api_service';


export function IntegrityVerificationPanel({ events }) {
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);

  const verifyIntegrity = async () => {
    setVerifying(true);
    try {
      const data = await apiFetch(`/v1/audit/verify-chain`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      setResult({
        isValid: data.valid !== false,
        totalChecked: data.events_checked || events.length,
        valid: data.valid !== false ? (data.events_checked || events.length) : 0,
        invalid: data.valid === false ? 1 : 0,
        issues: data.issues || [],
        checkedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Verification failed:', error);
      setResult({
        isValid: false,
        totalChecked: 0,
        valid: 0,
        invalid: 1,
        issues: [{ eventId: 'N/A', issue: error.message }],
        checkedAt: new Date().toISOString()
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div 
      className="bg-gradient-to-br from-[#0A1628] to-[#111827] border border-emerald-500/30 rounded-xl p-6"
      data-testid="integrity-verification-panel"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-emerald-500/20">
          <Shield className="h-5 w-5 text-emerald-400" />
        </div>
        <div>
          <h3 className="font-semibold text-lg text-white">Cryptographic Integrity Verification</h3>
          <p className="text-sm text-[#6B6B6B]">
            Verify the immutability and integrity of the audit trail
          </p>
        </div>
      </div>

      {!result ? (
        <div className="text-center py-6">
          <p className="text-sm text-[#6B6B6B] mb-4">
            Click to verify {events.length} event{events.length !== 1 ? 's' : ''} in the chain
          </p>
          <button
            onClick={verifyIntegrity}
            disabled={verifying}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium disabled:opacity-50 transition-colors"
            data-testid="verify-integrity-btn"
          >
            {verifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <Shield className="h-4 w-4" />
                Verify Chain Integrity
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Result Banner */}
          <div className={`p-4 rounded-lg border-2 ${
            result.isValid 
              ? 'bg-emerald-500/10 border-emerald-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-start gap-3">
              {result.isValid ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-red-400 shrink-0" />
              )}
              <div>
                <p className="font-semibold text-base text-white">
                  {result.isValid ? '✓ Integrity Verified' : '⚠️ Integrity Issues Detected'}
                </p>
                <p className="text-sm text-[#A0A0A0] mt-1">
                  {result.isValid
                    ? 'All events have valid cryptographic hashes and chain continuity.'
                    : `${result.invalid} issue(s) found in the audit chain.`}
                </p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 rounded-lg bg-[#111111] border border-[#1F1F1F]">
              <p className="text-xs text-[#6B6B6B]">Total Checked</p>
              <p className="text-2xl font-bold text-white">{result.totalChecked}</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-xs text-[#6B6B6B]">Valid</p>
              <p className="text-2xl font-bold text-emerald-400">{result.valid}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-[#6B6B6B]">Invalid</p>
              <p className="text-2xl font-bold text-red-400">{result.invalid}</p>
            </div>
          </div>

          {/* Issues List */}
          {result.issues && result.issues.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">Issues Found:</p>
              {result.issues.map((issue, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-sm font-medium text-red-400">
                    Event: {issue.eventId}
                  </p>
                  <p className="text-xs text-red-300 mt-1">{issue.issue}</p>
                </div>
              ))}
            </div>
          )}

          {/* Timestamp */}
          <p className="text-xs text-[#6B6B6B] text-center">
            Verified at {new Date(result.checkedAt).toLocaleString()}
          </p>

          <button
            onClick={verifyIntegrity}
            disabled={verifying}
            className="w-full py-2 rounded-lg border border-[#1F1F1F] hover:bg-[#1F1F1F] text-white text-sm transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${verifying ? 'animate-spin' : ''}`} />
            Re-verify
          </button>
        </div>
      )}
    </div>
  );
}

export default IntegrityVerificationPanel;
